import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Checkbox from "@material-ui/core/Checkbox";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import {
  ModalTitle,
  FormField,
  SwitchRow,
  Callout,
  usePanelStyles
} from "../QueueIntegrationModal/panelKit";

const PURPOSES = [
  { value: "comprovante", label: "Comprovante de pagamento (PIX, boleto, TED)" },
  { value: "onu", label: "Foto da ONU / roteador (LEDs, modelo, serial)" },
  { value: "livre", label: "Leitura livre (a IA identifica o que é)" }
];

const VARS = {
  comprovante: [
    "meio",
    "valor",
    "data",
    "hora",
    "pagador",
    "beneficiario",
    "beneficiario_documento",
    "banco",
    "autenticacao",
    "situacao"
  ],
  onu: [
    "modelo",
    "serial",
    "led_power",
    "led_pon",
    "led_los",
    "led_lan",
    "led_wifi",
    "diagnostico",
    "cabos_conectados"
  ],
  livre: ["(campos de comprovante e de ONU quando reconhecidos, mais os da instrução)"]
};

const DEFAULT_QUESTIONS = {
  comprovante: "Envie a *foto* ou o *PDF* do comprovante de pagamento, por favor.",
  onu: "Envie uma *foto* da frente do seu aparelho de internet (ONU/roteador), com as luzes visíveis.",
  livre: "Envie a *foto* ou o *PDF*, por favor."
};

const slugVar = s =>
  String(s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Za-z0-9_]/g, "_")
    .replace(/^_+|_+$/g, "");

/** Modal do nó "Ler arquivo (IA)". Grava { question, purpose, instruction, variableName, note } em node.data. */
const FlowBuilderReadMediaModal = ({ open, onSave, data, onUpdate, close }) => {
  const panel = usePanelStyles();
  const [activeModal, setActiveModal] = useState(false);
  const [purpose, setPurpose] = useState("comprovante");
  const [question, setQuestion] = useState("");
  const [instruction, setInstruction] = useState("");
  const [variableName, setVariableName] = useState("comprovante");
  const [note, setNote] = useState(true);

  useEffect(() => {
    if (open === "edit" && data?.data) {
      setPurpose(data.data.purpose || "livre");
      setQuestion(data.data.question || "");
      setInstruction(data.data.instruction || "");
      setVariableName(data.data.variableName || "arquivo");
      setNote(data.data.note !== false);
      setActiveModal(true);
    } else if (open === "create") {
      setPurpose("comprovante");
      setQuestion("");
      setInstruction("");
      setVariableName("comprovante");
      setNote(true);
      setActiveModal(true);
    }
  }, [open, data]);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const handlePurpose = p => {
    setPurpose(p);
    // Só troca o nome da variável se o usuário ainda não personalizou.
    if (!variableName || ["comprovante", "onu", "livre", "arquivo"].includes(variableName)) {
      setVariableName(p === "livre" ? "arquivo" : p);
    }
  };

  const handleSave = () => {
    const v = slugVar(variableName);
    if (!v) return toast.error("Informe o nome da variável");
    const payload = {
      purpose,
      question: question.trim(),
      instruction: instruction.trim(),
      variableName: v,
      note
    };
    if (open === "edit") onUpdate({ ...data, data: payload });
    else onSave(payload);
    handleClose();
  };

  const v = slugVar(variableName) || "arquivo";
  const varsHelp =
    "Variáveis geradas: ${" + v + ".resumo}, ${" + v + ".tipo}, ${" + v + ".confianca}" +
    (VARS[purpose] || []).map(f => ", ${" + v + "." + f + "}").join("") +
    "\nSe o cliente não mandar arquivo (2 tentativas) ou a IA não conseguir ler, o fluxo segue pela saída \"Não lido\" com ${" +
    v +
    ".erro}." +
    "\nFoto (jpg/png/webp) e PDF com texto. Vídeo ainda não.";

  return (
    <Dialog open={activeModal} onClose={handleClose} fullWidth maxWidth="sm" scroll="paper">
      <ModalTitle
        title={
          open === "create"
            ? "Adicionar leitura de arquivo (IA)"
            : "Editar leitura de arquivo (IA)"
        }
        subtitle="Pede uma foto ou PDF ao cliente e a IA lê o conteúdo para o fluxo."
        onClose={handleClose}
      />
      <DialogContent>
        <div className={panel.stack}>
          <FormField label="O que o cliente vai enviar">
            <FormControl variant="outlined" size="small" fullWidth>
              <Select
                id="flow-readmedia-purpose"
                value={purpose}
                onChange={e => handlePurpose(e.target.value)}
              >
                {PURPOSES.map(p => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </FormField>

          <FormField label="Mensagem pedindo o arquivo" htmlFor="flow-readmedia-question">
            <TextField
              id="flow-readmedia-question"
              variant="outlined"
              size="small"
              fullWidth
              multiline
              minRows={2}
              placeholder={DEFAULT_QUESTIONS[purpose]}
              value={question}
              onChange={e => setQuestion(e.target.value)}
            />
          </FormField>

          <FormField
            label="Instrução extra para a IA"
            optional="opcional"
            htmlFor="flow-readmedia-instruction"
          >
            <TextField
              id="flow-readmedia-instruction"
              variant="outlined"
              size="small"
              fullWidth
              multiline
              minRows={2}
              placeholder={
                "Ex.: numero_os: número da ordem de serviço\nUma linha por campo extra, no formato nome: descrição"
              }
              value={instruction}
              onChange={e => setInstruction(e.target.value)}
            />
          </FormField>

          <FormField
            label="Nome da variável"
            htmlFor="flow-readmedia-variable"
            help={"Só letras, números e _ . Use nos próximos nós como ${" + v + ".campo}"}
          >
            <TextField
              id="flow-readmedia-variable"
              variant="outlined"
              size="small"
              fullWidth
              value={variableName}
              onChange={e => setVariableName(e.target.value)}
            />
          </FormField>

          <SwitchRow label="Deixar nota interna no ticket com o que a IA leu">
            <Checkbox
              checked={note}
              onChange={e => setNote(e.target.checked)}
              color="primary"
              inputProps={{ "aria-label": "Deixar nota interna no ticket com o que a IA leu" }}
            />
          </SwitchRow>

          <Callout icon={<InfoOutlinedIcon />}>
            <span style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{varsHelp}</span>
          </Callout>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button onClick={handleSave} color="primary" variant="contained">
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FlowBuilderReadMediaModal;
